// Contrato de sincronización del hub (2026-09-22, ver docs/modulos/ventas-hub.md — "Invalidación
// cross-tab de datos de ventas"): el hub mantiene sus 5 tabs montadas (keep-alive) y cada una
// carga sus datos UNA sola vez — si una tab escribe una oportunidad (cambia estatus, la elimina,
// etc.) y el usuario pasa a otra tab sin recargar la página, esa otra tab seguía mostrando el
// dato viejo: los datos en la base estaban bien, la PANTALLA mentía.
//
// Mecanismo: un contador de versión compartido, al lado del `HubHeaderContext` que ya existe
// (mismo patrón — contexto propio del hub, no-op fuera de él). Cada tab que LEE oportunidades
// recuerda con qué versión cargó; al volverse visible, si la versión compartida cambió desde
// entonces, recarga — nunca mientras está oculta. Cada tab que ESCRIBE una oportunidad incrementa
// el contador tras el éxito, y a la vez marca su propia copia como "al día" con esa nueva versión
// (ya aplicó el cambio de forma optimista) — así nunca se recarga a sí misma por su propio bump.
//
// Toda tab NUEVA que lea y/o escriba oportunidades tiene que respetar este contrato — ver la
// ficha para el patrón exacto de uso.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const VentasDataVersionContext = createContext(null);

export const VentasDataVersionProvider = ({ children }) => {
  // Ref, no useState: el contador en sí NUNCA necesita disparar un re-render del hub — cada tab
  // lo consulta por su cuenta cuando le corresponde (al volverse activa). Si fuera useState acá,
  // CADA escritura re-renderizaría el hub entero (las 5 tabs, aunque estén ocultas) sin necesidad.
  const versionRef = useRef(0);
  // Objeto de valor ESTABLE (nunca cambia de referencia entre renders): evita que los `useEffect`
  // de los consumidores que lo tengan como dependencia se re-disparen sin motivo.
  const valueRef = useRef({
    getVersion: () => versionRef.current,
    bump: () => {
      versionRef.current += 1;
      return versionRef.current;
    },
  });
  return <VentasDataVersionContext.Provider value={valueRef.current}>{children}</VentasDataVersionContext.Provider>;
};

/**
 * Hook que usa cada tab del hub que lee y/o escribe oportunidades.
 * @param {boolean} active - si esta tab es la actualmente visible del hub (key === activeKey).
 * @returns {{
 *   bump: () => void,                // llamar tras CUALQUIER escritura propia exitosa de una
 *                                     // oportunidad (estatus, eliminación, edición) — nunca antes
 *                                     // de confirmar que la escritura tuvo éxito.
 *   stale: boolean,                  // true si, estando `active`, otra tab escribió algo desde la
 *                                     // última vez que esta tab se sincronizó. Se resetea solo con
 *                                     // marcarSincronizada().
 *   marcarSincronizada: () => void,  // llamar cuando la carga/recarga de datos de la tab termina.
 * }}
 * Fuera del hub (sin VentasDataVersionProvider por encima, ej. las páginas standalone huérfanas)
 * es un no-op completo: bump no hace nada, stale siempre false, marcarSincronizada no hace nada —
 * el comportamiento standalone queda exactamente igual que hoy, sin este mecanismo.
 */
export const useVentasDataSync = (active) => {
  const ctx = useContext(VentasDataVersionContext);
  // Arranca "al día" con la versión que tenga el hub en el momento del montaje — evita un falso
  // `stale=true` en el primer render, antes de que esta tab haya cargado nada todavía (su propia
  // carga inicial no pasa por este mecanismo, es la de siempre).
  const lastSyncedRef = useRef(ctx ? ctx.getVersion() : 0);
  const [stale, setStale] = useState(false);

  const marcarSincronizada = useCallback(() => {
    if (ctx) lastSyncedRef.current = ctx.getVersion();
    setStale(false);
  }, [ctx]);

  const bump = useCallback(() => {
    if (!ctx) return;
    // La tab que originó el cambio ya aplicó el movimiento de forma optimista — se marca a sí
    // misma "al día" con la nueva versión EN EL MISMO momento en que la publica, para que su
    // propio chequeo de abajo nunca la vea desactualizada por su propio bump.
    lastSyncedRef.current = ctx.bump();
  }, [ctx]);

  // Chequeo SOLO mientras la tab está activa — nunca mientras está oculta (si el usuario está en
  // otra tab cuando esto cambia, no pasa nada acá; se chequea recién cuando `active` pase a true).
  useEffect(() => {
    if (!ctx || !active) return;
    if (ctx.getVersion() !== lastSyncedRef.current) setStale(true);
  }, [ctx, active]);

  return { bump, stale, marcarSincronizada };
};
